/**
 * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
 * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
 *
 * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
 */

import pluginFactory from 'taoTests/runner/plugin';
import { mount, unmount } from 'svelte';
import { debounce, merge } from 'lodash';
import { getItemProperty } from '../../../util/testMap.js';
import { ResizeObserver } from '../../../polyfills.js';
import { getTestSessionUserDataService } from '../../../session/testSessionUserDataService.js';
import settingsKeys from '../../settings/settingsKeys.js';
import Watermark from './Watermark.svelte';
import { formatHash, hashText } from './hash.js';
import { createProps } from '../../../../svelteUtils/props.svelte.js';

const WATERMARK_CATEGORY = 'x-tao-option-watermark';
const REFRESH_WAIT = 100;

const defaultConfig = {
    alwaysShow: false,
    type: 'horizontal',
    layer: 'foreground',
    style: '',
    containerStyle: '',
    textPartLength: 10,
    separatorsLength: 3,
    configsByCategory: {}
};

/**
 * @param {Object} testRunner
 * @returns {string[]}
 */
function getCurrentItemCategories(testRunner) {
    const testMap = testRunner.getTestMap();
    const { testPartId, sectionId, itemIdentifier } = testRunner.getTestContext();
    const categories = getItemProperty(testMap, testPartId, sectionId, itemIdentifier, 'categories');

    return Array.isArray(categories) ? categories : [];
}

/**
 * @param {string} [suffix]
 * @returns {string}
 */
function getWatermarkCategory(suffix) {
    return suffix ? `${WATERMARK_CATEGORY}-${suffix}` : WATERMARK_CATEGORY;
}

/**
 * @param {string[]} categories
 * @param {string} [suffix]
 * @returns {boolean}
 */
function hasWatermarkCategory(categories, suffix) {
    return categories.includes(getWatermarkCategory(suffix));
}

/**
 * @param {Object} baseConfig
 * @param {string[]} categories
 * @returns {Object|null}
 */
function getEffectiveConfig(baseConfig, categories) {
    if (baseConfig.alwaysShow) {
        return baseConfig;
    }

    const configsByCategory = baseConfig.configsByCategory || {};

    for (const [suffix, profileConfig] of Object.entries(configsByCategory)) {
        if (hasWatermarkCategory(categories, suffix)) {
            return merge({}, baseConfig, profileConfig);
        }
    }

    return hasWatermarkCategory(categories) ? baseConfig : null;
}

/**
 * The watermark plugin renders a hashed test taker login over the assessment area.
 */
export default pluginFactory({
    name: 'watermark',

    install() {
        const testRunner = this.getTestRunner();
        const testConfig = testRunner.getConfig();

        this.component = null;
        this.resizeObserver = null;
        this.settingsStoreUnsubscribe = null;
        this.renderToken = 0;
        this.refreshKey = 0;
        this.textHash = null;
        this.a11ySettings = {
            pageZoom: null,
            fontSize: null
        };

        /**
         * @returns {Object}
         */
        this.getBaseConfig = () => merge({}, defaultConfig, testRunner.getPluginConfig(this.getName()) || {});

        /**
         * @param {Object} config
         * @returns {Promise<string>}
         */
        this.getText = config => {
            const source = String(testConfig.testTaker?.login || testConfig.testTaker?.id || 'anonymous');

            if (this.textHash) {
                return Promise.resolve(formatHash(this.textHash, config.textPartLength, config.separatorsLength));
            }

            return hashText(source).then(hash => {
                this.textHash = hash;

                return formatHash(hash, config.textPartLength, config.separatorsLength);
            });
        };

        /**
         * @returns {Object}
         */
        this.getWatermarkRect = () => {
            const mainArea = this.getAreaBroker().getMainArea();
            const rect = mainArea?.getBoundingClientRect();

            return {
                top: rect?.top || 0,
                left: rect?.left || 0,
                width: rect?.width || 0,
                height: rect?.height || 0
            };
        };

        this.updateRect = () => {
            if (this.componentProps) {
                this.componentProps.rect = this.getWatermarkRect();
                this.componentProps.refreshKey = ++this.refreshKey;
            }
        };
        this.updateRectDebounced = debounce(this.updateRect, REFRESH_WAIT);

        /**
         * @param {Object} settings
         * @returns {{pageZoom: *, fontSize: *}}
         */
        this.getA11ySettings = settings => {
            const a11yMenuSettings = settings?.[settingsKeys.a11yMenuPanel] || {};

            return {
                pageZoom: settings?.[settingsKeys.pageZoom] || a11yMenuSettings[settingsKeys.pageZoom],
                fontSize: settings?.[settingsKeys.fontSize] || a11yMenuSettings[settingsKeys.fontSize]
            };
        };

        /**
         * @param {Object} settings
         */
        this.handleSettingsChange = settings => {
            const a11ySettings = this.getA11ySettings(settings);
            const a11ySettingsChanged =
                a11ySettings.pageZoom !== this.a11ySettings.pageZoom ||
                a11ySettings.fontSize !== this.a11ySettings.fontSize;

            this.a11ySettings = a11ySettings;

            if (a11ySettingsChanged && this.component) {
                this.updateRectDebounced();
            }
        };

        this.startObservers = () => {
            const areaBroker = this.getAreaBroker();
            const mainArea = areaBroker.getMainArea();

            if (!mainArea || this.resizeObserver) {
                return;
            }

            this.resizeObserver = new ResizeObserver(this.updateRectDebounced);
            this.resizeObserver.observe(mainArea);
        };

        this.stopObservers = () => {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
            this.resizeObserver = null;

            this.updateRectDebounced.cancel();
        };

        this.hide = () => {
            this.renderToken++;
            this.stopObservers();

            if (this.component) {
                unmount(this.component);
            }
            this.component = null;
        };

        this.render = () => {
            const config = getEffectiveConfig(this.getBaseConfig(), getCurrentItemCategories(testRunner));

            if (!config) {
                this.hide();
                return;
            }

            const renderToken = ++this.renderToken;

            this.getText(config)
                .then(text => {
                    if (renderToken !== this.renderToken) {
                        return;
                    }

                    const areaBroker = this.getAreaBroker();
                    const mainArea = areaBroker.getMainArea();

                    if (!mainArea) {
                        return;
                    }

                    if (this.component && this.componentProps) {
                        this.componentProps.text = text;
                        this.componentProps.config = config;
                        this.componentProps.rect = this.getWatermarkRect();
                    } else {
                        this.componentProps = createProps({
                            text,
                            config,
                            rect: this.getWatermarkRect()
                        });
                        this.component = mount(Watermark, {
                            target: mainArea,
                            props: this.componentProps
                        });
                    }

                    this.startObservers();
                })
                .catch(error => testRunner.trigger('error', error));
        };
    },

    init() {
        const serviceCallId = this.getTestRunner().getConfig().serviceCallId;
        const settingsStore = getTestSessionUserDataService(serviceCallId).getSettingsStore();

        this.settingsStoreUnsubscribe = settingsStore.subscribe(this.handleSettingsChange);

        this.getTestRunner()
            .on(`renderitem.${this.getName()}`, this.render)
            .on(`unloaditem.${this.getName()} itemModalFeedback.${this.getName()}`, this.hide);
    },

    destroy() {
        this.hide();
        if (typeof this.settingsStoreUnsubscribe === 'function') {
            this.settingsStoreUnsubscribe();
        }
        this.getTestRunner().off(`.${this.getName()}`);
    }
});
